"use client";
import React, {createContext, useContext, useEffect, useState, ReactNode} from "react";

export type SupportedModules = "preview" | "apps" | "agents" | "workflows";

export interface Tenant {
  _id: string;
  name: string;
  handle: string;
  description?: string;
  admins: string[];
  users: string[];
  createdBy: string;
  logoUrl?: string;
  modules?: SupportedModules[];
  
  //ui fields
  selected: boolean;
  plan?: string;
}

export type User = {
  _id: string;
  email: string;
  name: string;
  thumbnail?: string;
  picture?: string;
  jwtToken: string;
  onecompilerUserId?: string;
  onecompilerUserToken?: string;
  tenants?: Tenant[];
};

interface UserContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  hydrated: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const USER_STORAGE_KEY = "rtzen_user";

export function UserProvider({children}: {children: ReactNode}) {
  const [user, setUser] = useState<User | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem(USER_STORAGE_KEY) : null;
    if (stored) {
      try {
        setUser(JSON.parse(stored));
      } catch {}
    }
    setHydrated(true);
  }, []);

  // Sync to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  }, [user]);

  const login = (userData: User) => setUser(userData);
  const logout = () => setUser(null);
  const updateUser = (updates: Partial<User>) => setUser(prev => (prev ? {...prev, ...updates} : prev));

  return <UserContext.Provider value={{user, login, logout, updateUser, hydrated}}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within a UserProvider");
  return ctx;
}
